import { splitWords } from '../composer/composer';
import type { CandidatePhrase, HistoryEntry } from '../types';

/**
 * Phrase mining — the person's own repeated sentences becoming one-tap buttons.
 *
 * 2.4 calls this "the single most valuable behaviour in the phase", and that is not hyperbole: a
 * sentence this person types three times is a sentence they will type again, and turning it into a tile
 * converts a minute of typing into one tap. Nothing else in Phase 2 has that leverage.
 *
 * Pure: history in, candidates out. No storage, no clock beyond the `now` it is handed.
 *
 * ── Whole utterances, not every window (C45) ──
 *
 * 2.4 says "n-gram counting over utterances, min length 3 words". Counting every window produces a pile
 * of overlapping candidates from one sentence — "Ich habe Schmerzen im Rücken" yields six, all with the
 * same count — so sub-sequences are suppressed and what survives is the complete sentence. That matches
 * §5.4's "cluster repeated multi-word sequences", and it means **every candidate is a complete, sayable
 * sentence**, which is what a phrase tile has to be.
 *
 * What it gives up: window counting would also find a reusable *stem* across variations. Someone who
 * says "…im Rücken" twice and "…im Bein" twice gets nothing here, where windows would surface the shared
 * opening at four. That is a real feature and a deliberate omission — if the queue looks thin after real
 * use, stems are the measured next step rather than a guess now.
 */

/** 2.4's threshold: spoken at least this often, within the window below. */
export const MIN_OCCURRENCES = 3;
export const WINDOW_DAYS = 14;
/** Below this, a "repeated sentence" is a repeated word or two and not worth a tile. */
export const MIN_WORDS = 3;

/**
 * Sources that never contribute.
 *
 * `phrase` is excluded for a reason that is easy to miss: a phrase tap would mine the phrase back into a
 * candidate for itself, so the queue would fill with suggestions to create tiles that already exist.
 * `quickFire` is excluded for the same reason it does not teach the n-grams.
 */
const MINEABLE_SOURCES = new Set(['keyboard', 'prediction']);

/** Comparison key: case- and punctuation-insensitive, so one sentence is one candidate. */
export function normaliseForMining(text: string): string {
  return splitWords(text).join(' ').toLowerCase();
}

export interface MiningInput {
  history: readonly HistoryEntry[];
  /** Existing candidates, so dismissals survive and counts accumulate. */
  existing: readonly CandidatePhrase[];
  /** Phrases already on the board — never propose one of those again. */
  existingPhraseTexts: readonly string[];
  nowMs: number;
}

/**
 * Candidates worth showing a caregiver, most repeated first.
 *
 * Dismissed candidates are returned unchanged rather than dropped: the store is also the record of what
 * has been rejected, and 2.4 requires that a dismissal **never resurfaces**.
 */
export function mineCandidates(input: MiningInput): CandidatePhrase[] {
  const cutoff = input.nowMs - WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const dismissed = new Set(
    input.existing.filter((candidate) => candidate.dismissed).map((candidate) => normaliseForMining(candidate.text)),
  );
  const alreadyPhrases = new Set(input.existingPhraseTexts.map(normaliseForMining));

  /** normalised → the occurrences inside the window */
  const groups = new Map<string, { text: string; times: number[] }>();

  for (const entry of input.history) {
    if (!MINEABLE_SOURCES.has(entry.source)) continue;

    const spokenAt = Date.parse(entry.spokenAt);
    // The date cutoff is what keeps this O(window) rather than O(all history). History itself is never
    // pruned — it is the person's own record — so the query has to be the thing that stays bounded.
    if (!Number.isFinite(spokenAt) || spokenAt < cutoff) continue;

    const words = splitWords(entry.text);
    if (words.length < MIN_WORDS) continue;

    const key = normaliseForMining(entry.text);
    if (key === '' || dismissed.has(key) || alreadyPhrases.has(key)) continue;

    const group = groups.get(key);
    if (group) {
      group.times.push(spokenAt);
    } else {
      // Keep the text as the person actually wrote it, not the normalised key: the tile should read the
      // way they say it, capitals and punctuation included.
      groups.set(key, { text: entry.text.trim(), times: [spokenAt] });
    }
  }

  const qualifying = [...groups.entries()]
    .filter(([, group]) => group.times.length >= MIN_OCCURRENCES)
    .map(([key, group]) => ({
      key,
      text: group.text,
      count: group.times.length,
      firstSeen: new Date(Math.min(...group.times)).toISOString(),
      lastSeen: new Date(Math.max(...group.times)).toISOString(),
    }));

  const kept = suppressSubSequences(qualifying);

  // Preserve any prior record of a candidate, so a count never appears to go backwards across runs.
  const previous = new Map(input.existing.map((candidate) => [normaliseForMining(candidate.text), candidate]));

  const fresh: CandidatePhrase[] = kept.map((candidate) => {
    const before = previous.get(candidate.key);
    return {
      text: candidate.text,
      count: candidate.count,
      firstSeen: before?.firstSeen ?? candidate.firstSeen,
      lastSeen: candidate.lastSeen,
      dismissed: false,
    };
  });

  const dismissedRows = input.existing.filter((candidate) => candidate.dismissed);

  return [...fresh.sort((a, b) => b.count - a.count), ...dismissedRows];
}

interface Qualifying {
  key: string;
  text: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
}

/**
 * Drop any candidate contained in another that is at least as frequent.
 *
 * This is the "cluster" in §5.4. If "Ich habe Schmerzen" and "Ich habe Schmerzen im Rücken" both appear
 * three times, only the longer one is worth a tile — the shorter is the same sentence, cut off. But if
 * the shorter one is genuinely *more* frequent, it stands on its own: the person says it independently.
 */
function suppressSubSequences(candidates: readonly Qualifying[]): Qualifying[] {
  return candidates.filter((candidate) =>
    !candidates.some(
      (other) =>
        other.key !== candidate.key &&
        other.count >= candidate.count &&
        other.key.length > candidate.key.length &&
        containsWordSequence(other.key, candidate.key),
    ),
  );
}

/** Word-boundary containment, so "im Bein" is not found inside "Schlimm Beinbruch". */
function containsWordSequence(haystack: string, needle: string): boolean {
  const outer = haystack.split(' ');
  const inner = needle.split(' ');
  if (inner.length > outer.length) return false;

  for (let start = 0; start + inner.length <= outer.length; start += 1) {
    let matched = true;
    for (let offset = 0; offset < inner.length; offset += 1) {
      if (outer[start + offset] !== inner[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return true;
  }
  return false;
}
